/**
 * L'écran d'un annuaire — les clients, les transporteurs, et demain les sites.
 *
 * Conforme à la maquette validée le 03/09/2026, versionnée dans
 * `docs/maquettes/clients.html` : navigation en haut, liste en pleine largeur
 * tant que rien n'est choisi, fiche sur la moitié droite dès qu'on clique une
 * ligne, en quatre onglets — Identité · Contacts · Conditions · Activité.
 *
 * **Un seul écran pour tous les annuaires**, comme `apps/api/src/annuaire.ts`
 * ne fabrique qu'un jeu de routes : l'écran d'un transporteur est celui d'un
 * client à quelques mots près — le segment d'URL, la liste des champs, deux
 * phrases dont le sens s'inverse. Deux fichiers jumeaux de mille lignes
 * finiraient par diverger sans que personne ne le voie.
 *
 * L'état est tenu à la main, décidé le 03/09/2026 — même raisonnement que pour
 * le routeur de l'API : trente lignes qu'on lit entièrement plutôt qu'une
 * dépendance qu'on ne lit pas. Il y a peu à tenir : la liste, la fiche ouverte,
 * l'onglet courant, la recherche.
 *
 * Ce que ce fichier ne fait pas : calculer. Le filtrage, la mise en forme des
 * montants et des dates, le décompte sont dans `annuaire.ts`, en fonctions pures
 * — c'est ce qui les rend vérifiables sans navigateur.
 *
 * Le fichier se coupe en deux :
 *
 *   - `VueAnnuaire` ne fait que dessiner. On lui donne un état, elle rend du
 *     HTML, et rien d'autre. C'est ce qui permet de **la rendre et de la
 *     regarder** hors d'un navigateur, avec n'importe quel état — liste vide,
 *     fiche en panne, fiche désactivée — sans monter une base ni une session ;
 *   - `EcranAnnuaire` tient l'état, appelle l'API, et lui passe le résultat.
 */

import { useEffect, useState } from 'react';
import {
  changerActivite,
  creerContact,
  creerFiche,
  enregistrerVehicules,
  enregistrerZones,
  lireZones,
  lireFiche,
  lireListe,
  lireVehicules,
  modifierContact,
  modifierFiche,
  supprimerContact,
  supprimerFiche,
  type Contact,
  type Zone,
  type Dossier,
  type EchecApi,
  type LigneListe,
  type Vehicule,
} from './api.js';
import {
  basculerCode,
  basculerZone,
  codePropose,
  contactDepuis,
  decompte,
  difference,
  differenceContact,
  enEuros,
  filtrer,
  frequenceDite,
  fusionner,
  localite,
  messageCodePris,
  messageRattachement,
  porte,
  pourCreation,
  pourCreationContact,
  saisieDepuis,
  saisieVide,
  valeur,
  validerContact,
  validerFiche,
  CONTACT_VIDE,
  type Annuaire,
  type CleChampContact,
  type Saisie,
} from './annuaire.js';
import { FormulaireFiche } from './FormulaireFiche.js';
import { OngletContacts, type EditionContact } from './OngletContacts.js';
import { OngletCouverture, type EditionCouverture } from './OngletCouverture.js';

const ONGLETS = [
  { cle: 'identite', nom: 'Identité' },
  { cle: 'contacts', nom: 'Contacts' },
  { cle: 'couverture', nom: 'Couverture' },
  { cle: 'conditions', nom: 'Conditions' },
  { cle: 'activite', nom: 'Activité' },
] as const;

type CleOnglet = (typeof ONGLETS)[number]['cle'];

/**
 * Les onglets d'un annuaire donné.
 *
 * « Couverture » n'existe que là où il y a quelque chose à couvrir, et
 * « Conditions » que là où il y a quelque chose à régler : c'est le
 * descripteur qui le dit, et non un `if` sur le nom de l'annuaire. Un client
 * en a donc quatre, un transporteur cinq, un site trois.
 */
function ongletsDe(annuaire: Annuaire): readonly (typeof ONGLETS)[number][] {
  return ONGLETS.filter((o) => {
    if (o.cle === 'couverture') return annuaire.couverture;
    if (o.cle === 'conditions') return annuaire.conditions;
    return true;
  });
}

interface Proprietes {
  /** Quel annuaire cet écran dessine. C'est le seul paramètre qui l'en change. */
  readonly annuaire: Annuaire;
  /** Rend un jeton frais. `auth-js` le renouvelle seul tant que l'onglet vit. */
  readonly jeton: () => Promise<string | null>;
  /** Appelé quand l'API refuse l'accès : l'aiguillage renvoie à la connexion. */
  readonly quandAccesPerdu: () => void;
}

type EtatListe =
  | { readonly nom: 'chargement' }
  | { readonly nom: 'prete'; readonly lignes: readonly LigneListe[] }
  | { readonly nom: 'panne' };

/**
 * Le formulaire, quand il est ouvert. `initiale` garde la saisie de départ :
 * c'est elle qui permet de n'envoyer que ce qui a changé.
 */
type Edition =
  | { readonly nom: 'aucune' }
  | {
      readonly nom: 'creation' | 'modification';
      readonly id: string | null;
      readonly initiale: Saisie;
      readonly saisie: Saisie;
      readonly fautes: Readonly<Record<string, string>>;
      readonly refus: string | null;
      readonly envoi: boolean;
    };

type EtatFiche =
  | { readonly nom: 'aucune' }
  | { readonly nom: 'chargement'; readonly id: string }
  | { readonly nom: 'prete'; readonly id: string; readonly dossier: Dossier }
  | { readonly nom: 'panne'; readonly id: string; readonly cause: EchecApi };

/** « non renseigné » en gris, plutôt qu'une case vide dont on ne sait rien. */
function Valeur({
  texte,
  chiffre,
}: {
  texte: string | null;
  chiffre?: boolean | undefined;
}) {
  if (texte === null) {
    return <dd className="absent">non renseigné</dd>;
  }
  return <dd className={chiffre === true ? 'chiffre' : undefined}>{texte}</dd>;
}

function Paire({
  etiquette,
  texte,
  chiffre,
}: {
  etiquette: string;
  texte: string | null;
  chiffre?: boolean | undefined;
}) {
  return (
    <>
      <dt>{etiquette}</dt>
      <Valeur texte={texte} chiffre={chiffre} />
    </>
  );
}

function OngletIdentite({
  dossier,
  annuaire,
}: {
  dossier: Dossier;
  annuaire: Annuaire;
}) {
  const f = dossier.fiche;
  return (
    <div className="deux-colonnes">
      {/* Un site n'est pas une entité facturée : ni SIRET ni n° de TVA dans son
          descripteur, donc pas de pavé Identification sur sa fiche. C'est le
          descripteur qui le dit, et non un `if` sur le nom de l'annuaire. */}
      {porte(annuaire, 'siret') && (
        <div className="section">
          <div className="section-titre">
            <span>Identification</span>
          </div>
          <dl className="paire">
            <Paire etiquette="SIRET" texte={valeur(f, 'siret')} chiffre />
            <Paire etiquette="N° de TVA" texte={valeur(f, 'tva')} chiffre />
          </dl>
        </div>
      )}

      <div className="section">
        <div className="section-titre">
          <span>Adresse</span>
        </div>
        <dl className="paire">
          <Paire etiquette="Voie" texte={valeur(f, 'adresse')} />
          <Paire etiquette="Commune" texte={localite(f.code_postal, f.ville)} />
          <Paire etiquette="Pays" texte={valeur(f, 'pays')} />
        </dl>
      </div>

      <div className="section">
        <div className="section-titre">
          <span>Standard</span>
        </div>
        <dl className="paire">
          <Paire etiquette="Téléphone" texte={f.telephone} chiffre />
          <Paire etiquette="Courriel" texte={f.courriel} />
          {porte(annuaire, 'site_web') && (
            <Paire etiquette="Site" texte={valeur(f, 'site_web')} />
          )}
        </dl>
      </div>

      {valeur(f, 'note') !== null && (
        <div className="section">
          <div className="section-titre">
            <span>Note</span>
          </div>
          <p className="note">{valeur(f, 'note')}</p>
        </div>
      )}
    </div>
  );
}

function OngletConditions({
  dossier,
  annuaire,
}: {
  dossier: Dossier;
  annuaire: Annuaire;
}) {
  const f = dossier.fiche;
  return (
    <>
      <div className="deux-colonnes">
        <div className="section">
          <div className="section-titre">
            <span>Règlement</span>
          </div>
          <dl className="paire">
            <Paire etiquette="Paiement" texte={valeur(f, 'conditions_paiement')} />
            {/* LA FRÉQUENCE SE LIT EN FRANÇAIS, pas en clé. La fiche montre la
                valeur enregistrée, et « par_mois » ne se lit pas. */}
            <Paire
              etiquette="Fréquence de facturation"
              texte={frequenceDite(valeur(f, 'frequence_facturation'))}
            />
            <Paire
              etiquette="Encours autorisé"
              texte={enEuros(valeur(f, 'encours_autorise'))}
              chiffre
            />
          </dl>
        </div>
      </div>
      {/* La phrase vient du descripteur : ce que le client nous doit, nous le
          devons au transporteur. Même colonne, sens contraire. */}
      <p className="pied-fiche">{annuaire.noteConditions ?? ''}</p>
    </>
  );
}

function OngletActivite({ dossier }: { dossier: Dossier }) {
  return (
    <div className="barre-onglet">
      <div className="section">
        <div className="section-titre">
          <span>Ce qui est rattaché</span>
        </div>
        <dl className="paire">
          <Paire etiquette="Contacts" texte={String(dossier.contacts.length)} chiffre />
        </dl>
      </div>
      <p className="a-construire">
        Les transports et les factures viendront ici, quand ces tables existeront. Ce
        sont ces décomptes qui commanderont la suppression : une fiche désactivée ne se
        supprimera que s’ils sont tous à zéro. Aujourd’hui seuls les contacts la
        retiennent.
      </p>
    </div>
  );
}

/**
 * L'état des actions de la fiche : désactiver, réactiver, supprimer.
 *
 * Un seul objet plutôt que trois états séparés — ces trois actions ne peuvent
 * pas se produire en même temps, et les tenir ensemble empêche d'oublier d'en
 * refermer une.
 */
export interface ActionsFiche {
  /** La confirmation de suppression est-elle posée sous les yeux ? */
  readonly confirme: boolean;
  readonly envoi: boolean;
  readonly refus: string | null;
  readonly quandBasculerActivite: () => void;
  readonly quandDemanderSuppression: () => void;
  readonly quandSupprimer: () => void;
  readonly quandAnnuler: () => void;
}

/**
 * Tout ce que l'onglet Contacts a besoin de savoir et de rapporter, en un seul
 * paquet : sans quoi douze propriétés de plus traverseraient le panneau et la
 * vue rien que pour arriver jusqu'à lui.
 */
/**
 * Tout ce que l'onglet Couverture a besoin de savoir et de rapporter, en un
 * seul paquet — même raison que pour les contacts : sans lui, dix propriétés
 * de plus traverseraient le panneau et la vue rien que pour arriver jusqu'à
 * lui.
 */
export interface PaquetCouverture {
  readonly referentielVehicules: readonly Vehicule[];
  readonly referentielZones: readonly Zone[];
  readonly edition: EditionCouverture;
  readonly quandVehicule: (code: string, coche: boolean) => void;
  readonly quandZone: (
    zone: string,
    sens: 'depart' | 'arrivee',
    coche: boolean,
  ) => void;
  readonly quandEnregistrerVehicules: () => void;
  readonly quandEnregistrerZones: () => void;
}

export interface Contacts {
  readonly edition: EditionContact;
  readonly quandAjouter: () => void;
  readonly quandModifier: (contact: Contact) => void;
  readonly quandDemanderSuppression: (id: string) => void;
  readonly quandChamp: (cle: CleChampContact, valeur: string) => void;
  readonly quandPrincipal: (coche: boolean) => void;
  readonly quandEnregistrer: () => void;
  readonly quandSupprimer: () => void;
  readonly quandAnnuler: () => void;
}

function Panneau({
  annuaire,
  etat,
  onglet,
  quandOnglet,
  quandFermer,
  quandModifier,
  contacts,
  couverture,
  actions,
}: {
  readonly annuaire: Annuaire;
  readonly etat: EtatFiche;
  readonly onglet: CleOnglet;
  readonly quandOnglet: (cle: CleOnglet) => void;
  readonly quandFermer: () => void;
  readonly quandModifier: () => void;
  readonly contacts: Contacts;
  readonly couverture: PaquetCouverture;
  readonly actions: ActionsFiche;
}) {
  if (etat.nom === 'aucune') {
    return null;
  }

  if (etat.nom === 'chargement') {
    return (
      <aside className="panneau">
        <div className="panneau-haut">
          <p className="attente">Chargement de la fiche…</p>
        </div>
      </aside>
    );
  }

  if (etat.nom === 'panne') {
    return (
      <aside className="panneau">
        <div className="panneau-haut">
          <div className="panneau-titre">
            <div>
              <div className="panneau-nom">Fiche indisponible</div>
            </div>
            <button className="fermer" type="button" onClick={quandFermer}>
              Fermer ✕
            </button>
          </div>
          <p className="bandeau">
            {etat.cause === 'introuvable'
              ? 'Cette fiche n’existe plus. Elle a pu être supprimée depuis l’affichage de la liste.'
              : 'Stafa ne répond pas. Vérifiez votre connexion et réessayez.'}
          </p>
        </div>
      </aside>
    );
  }

  const { dossier } = etat;
  const f = dossier.fiche;

  return (
    <aside className="panneau">
      <div className="panneau-haut">
        {!f.actif && (
          <p className="bandeau">
            Ce {annuaire.singulier} est désactivé. {annuaire.noteDesactive}
          </p>
        )}
        <div className="panneau-titre">
          <div>
            <div className="panneau-nom">{f.nom}</div>
            <div className="panneau-code">{f.code ?? 'sans code'}</div>
          </div>
          <button className="fermer" type="button" onClick={quandFermer}>
            Fermer ✕
          </button>
        </div>

        {actions.refus !== null && <p className="bandeau">{actions.refus}</p>}

        {/* Une fiche ACTIVE ne se supprime pas : on désactive d'abord. Règle
            arrêtée le 03/09/2026, et tenue aussi par l'API — qui répond
            `encore_actif` si on passe outre. */}
        <div className="actions">
          <button
            className={f.actif ? 'bouton' : 'bouton bouton-discret'}
            type="button"
            disabled={actions.envoi}
            onClick={quandModifier}
          >
            Modifier
          </button>
          <button
            className={f.actif ? 'bouton bouton-risque' : 'bouton bouton-discret'}
            type="button"
            disabled={actions.envoi}
            onClick={actions.quandBasculerActivite}
          >
            {f.actif ? 'Désactiver' : 'Réactiver'}
          </button>
          {!f.actif && (
            <button
              className="bouton bouton-risque"
              type="button"
              disabled={actions.envoi || dossier.contacts.length > 0}
              onClick={actions.quandDemanderSuppression}
            >
              Supprimer
            </button>
          )}
        </div>

        {!f.actif && !actions.confirme && (
          <p className="empechement">
            {messageRattachement(dossier.contacts.length, annuaire)}
          </p>
        )}

        {actions.confirme && (
          <div className="confirmation">
            <p className="empechement">
              Supprimer définitivement <strong>{f.nom}</strong> ? La fiche et son
              historique de modification seront perdus. Cette action est sans retour.
            </p>
            <div className="actions">
              <button
                className="bouton bouton-risque"
                type="button"
                disabled={actions.envoi}
                onClick={actions.quandSupprimer}
              >
                {actions.envoi ? 'Suppression…' : 'Supprimer définitivement'}
              </button>
              <button
                className="bouton bouton-discret"
                type="button"
                disabled={actions.envoi}
                onClick={actions.quandAnnuler}
              >
                Annuler
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="onglets" role="tablist">
        {ongletsDe(annuaire).map((o) => (
          <button
            className="onglet-fiche"
            type="button"
            role="tab"
            key={o.cle}
            aria-selected={o.cle === onglet}
            onClick={() => quandOnglet(o.cle)}
          >
            {o.nom}
          </button>
        ))}
      </div>

      <div className="panneau-corps">
        {onglet === 'identite' && (
          <OngletIdentite dossier={dossier} annuaire={annuaire} />
        )}
        {onglet === 'contacts' && (
          <OngletContacts contacts={dossier.contacts} {...contacts} />
        )}
        {onglet === 'couverture' && annuaire.couverture && (
          <OngletCouverture
            vehiculesEnregistres={dossier.vehicules ?? []}
            zonesEnregistrees={dossier.zones ?? []}
            {...couverture}
          />
        )}
        {onglet === 'conditions' && annuaire.conditions && (
          <OngletConditions dossier={dossier} annuaire={annuaire} />
        )}
        {onglet === 'activite' && <OngletActivite dossier={dossier} />}
      </div>
    </aside>
  );
}

interface ProprietesVue {
  readonly annuaire: Annuaire;
  readonly liste: EtatListe;
  readonly fiche: EtatFiche;
  readonly edition: Edition;
  readonly onglet: CleOnglet;
  readonly recherche: string;
  readonly quandRecherche: (texte: string) => void;
  readonly quandOuvrir: (id: string) => void;
  readonly quandOnglet: (cle: CleOnglet) => void;
  readonly quandFermer: () => void;
  readonly quandCreer: () => void;
  readonly quandModifier: () => void;
  readonly quandChamp: (cle: string, valeur: string) => void;
  readonly quandNomQuitte: () => void;
  readonly quandEnregistrer: () => void;
  readonly quandAnnuler: () => void;
  readonly contacts: Contacts;
  readonly couverture: PaquetCouverture;
  readonly actions: ActionsFiche;
}

/** Le dessin, et rien d'autre : aucun appel réseau, aucun état gardé. */
export function VueAnnuaire({
  annuaire,
  liste,
  fiche,
  edition,
  onglet,
  recherche,
  quandRecherche,
  quandOuvrir,
  quandOnglet,
  quandFermer,
  quandCreer,
  quandModifier,
  quandChamp,
  quandNomQuitte,
  quandEnregistrer,
  quandAnnuler,
  contacts,
  couverture,
  actions,
}: ProprietesVue) {
  const lignes = liste.nom === 'prete' ? liste.lignes : [];
  const visibles = filtrer(lignes, recherche);
  // Le formulaire prend la place de la fiche : même moitié d'écran.
  const ouverte = fiche.nom !== 'aucune' || edition.nom !== 'aucune';

  return (
    <div className={ouverte ? 'corps ouvert' : 'corps'}>
      <section className="liste">
        <div className="barre-outils">
          <input
            className="recherche"
            type="search"
            placeholder="Rechercher — nom, code, ville"
            aria-label={`Rechercher un ${annuaire.singulier}`}
            value={recherche}
            onChange={(e) => quandRecherche(e.target.value)}
          />
          <button className="bouton" type="button" onClick={quandCreer}>
            Créer un {annuaire.singulier}
          </button>
        </div>

        {/* Le décompte ne s'affiche que s'il y a des lignes à compter :
              « 0 client » posé au-dessus de « Aucun client n'est enregistré »
              fait doublon et sonne creux. Constaté à l'écran le 03/09/2026. */}
        {visibles.length > 0 && (
          <p className="decompte">{decompte(visibles, annuaire)}</p>
        )}

        {liste.nom === 'chargement' && (
          <div className="vide">
            <p>Chargement de l’annuaire…</p>
          </div>
        )}

        {liste.nom === 'panne' && (
          <div className="vide">
            <p>Stafa ne répond pas. Vérifiez votre connexion et réessayez.</p>
          </div>
        )}

        {liste.nom === 'prete' && lignes.length === 0 && (
          <div className="vide">
            <p>Aucun {annuaire.singulier} n’est enregistré.</p>
            <button className="bouton" type="button" onClick={quandCreer}>
              Créer un {annuaire.singulier}
            </button>
          </div>
        )}

        {liste.nom === 'prete' && lignes.length > 0 && visibles.length === 0 && (
          <div className="vide">
            <p>
              Aucun {annuaire.singulier} ne correspond à « {recherche.trim()} ».
            </p>
            <button
              className="bouton bouton-discret"
              type="button"
              onClick={() => quandRecherche('')}
            >
              Effacer la recherche
            </button>
          </div>
        )}

        {visibles.length > 0 && (
          <div className="tableau-cadre">
            <table>
              <thead>
                <tr>
                  <th scope="col" className="col-code">
                    Code
                  </th>
                  <th scope="col" className="col-nom">
                    Nom
                  </th>
                  <th scope="col" className="col-localite">
                    Localité
                  </th>
                  <th scope="col" className="col-tel col-large">
                    Téléphone
                  </th>
                  <th scope="col" className="col-courriel col-large">
                    Courriel
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((ligne) => {
                  const classes = ['rangee'];
                  if (!ligne.actif) classes.push('eteinte');
                  if (fiche.nom !== 'aucune' && fiche.id === ligne.id)
                    classes.push('choisie');

                  const lieu = localite(ligne.code_postal, ligne.ville);

                  return (
                    <tr
                      className={classes.join(' ')}
                      key={ligne.id}
                      tabIndex={0}
                      onClick={() => quandOuvrir(ligne.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          quandOuvrir(ligne.id);
                        }
                      }}
                    >
                      {ligne.code === null ? (
                        <td className="code-absent">sans code</td>
                      ) : (
                        <td className="code">{ligne.code}</td>
                      )}
                      <td className="nom">
                        {ligne.nom}
                        {!ligne.actif && <span className="pastille">inactif</span>}
                      </td>
                      {lieu === null ? (
                        <td className="sourdine">non renseigné</td>
                      ) : (
                        <td>{lieu}</td>
                      )}
                      {ligne.telephone === null ? (
                        <td className="sourdine col-large">non renseigné</td>
                      ) : (
                        <td className="tel col-large">{ligne.telephone}</td>
                      )}
                      {ligne.courriel === null ? (
                        <td className="sourdine col-large">non renseigné</td>
                      ) : (
                        <td className="col-large">{ligne.courriel}</td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {edition.nom === 'aucune' ? (
        <Panneau
          annuaire={annuaire}
          etat={fiche}
          onglet={onglet}
          quandOnglet={quandOnglet}
          quandFermer={quandFermer}
          quandModifier={quandModifier}
          contacts={contacts}
          couverture={couverture}
          actions={actions}
        />
      ) : (
        <FormulaireFiche
          champs={annuaire.champs}
          titre={
            edition.nom === 'creation'
              ? `Nouveau ${annuaire.singulier}`
              : // Le nom de la fiche, et non « Modifier la fiche » : le titre
                // doit dire laquelle on est en train de modifier.
                (edition.initiale['nom'] ?? '')
          }
          saisie={edition.saisie}
          fautes={edition.fautes}
          refus={edition.refus}
          envoi={edition.envoi}
          quandChamp={quandChamp}
          quandNomQuitte={quandNomQuitte}
          quandEnregistrer={quandEnregistrer}
          quandAnnuler={quandAnnuler}
        />
      )}
    </div>
  );
}

export function EcranAnnuaire({ annuaire, jeton, quandAccesPerdu }: Proprietes) {
  const [liste, setListe] = useState<EtatListe>({ nom: 'chargement' });
  const [fiche, setFiche] = useState<EtatFiche>({ nom: 'aucune' });
  // L'onglet ouvert survit au changement de client : on consulte souvent les
  // contacts de plusieurs clients à la suite (maquette du 03/09/2026).
  const [onglet, setOnglet] = useState<CleOnglet>('identite');
  const [recherche, setRecherche] = useState('');
  const [edition, setEdition] = useState<Edition>({ nom: 'aucune' });
  const [contact, setContact] = useState<EditionContact>({ nom: 'aucune' });
  const [action, setAction] = useState<{
    readonly confirme: boolean;
    readonly envoi: boolean;
    readonly refus: string | null;
  }>({ confirme: false, envoi: false, refus: null });

  // Les deux référentiels communs. Chargés UNE FOIS, et gardés : ils ne
  // changent pas d'une fiche à l'autre, ni d'une société à l'autre — seule une
  // migration peut les faire bouger.
  const [referentiels, setReferentiels] = useState<{
    readonly vehicules: readonly Vehicule[];
    readonly zones: readonly Zone[];
  }>({ vehicules: [], zones: [] });

  const [couverture, setCouverture] = useState<EditionCouverture>({
    vehicules: [],
    zones: [],
    envoi: 'aucun',
    refus: null,
  });

  useEffect(() => {
    let abandonne = false;

    void (async () => {
      const porteur = await jeton();
      if (porteur === null) {
        quandAccesPerdu();
        return;
      }
      const resultat = await lireListe(porteur, annuaire);
      if (abandonne) return;

      if (resultat.etat === 'ok') {
        setListe({ nom: 'prete', lignes: resultat.valeur });
      } else if (resultat.cause === 'acces') {
        quandAccesPerdu();
      } else {
        setListe({ nom: 'panne' });
      }
    })();

    return () => {
      abandonne = true;
    };
    // `annuaire` est dans les dépendances : changer d'annuaire dans la barre
    // haute recharge la liste, et remet l'écran à zéro par les trois `set`
    // ci-dessous. Sans lui, passer aux transporteurs garderait les clients à
    // l'écran.
  }, [annuaire, jeton, quandAccesPerdu]);

  // Changer d'annuaire referme tout ce qui appartenait au précédent : une fiche
  // ouverte, un formulaire en cours, une confirmation de suppression posée.
  useEffect(() => {
    setListe({ nom: 'chargement' });
    setFiche({ nom: 'aucune' });
    setEdition({ nom: 'aucune' });
    setContact({ nom: 'aucune' });
    setAction({ confirme: false, envoi: false, refus: null });
    setCouverture({ vehicules: [], zones: [], envoi: 'aucun', refus: null });
    // L'onglet Couverture n'existe pas partout : le garder ouvert en passant
    // aux clients laisserait un panneau vide sans onglet correspondant.
    setOnglet('identite');
    setRecherche('');
  }, [annuaire]);

  // Chargés seulement pour un annuaire qui porte une couverture : inutile de
  // demander 125 lignes à l'API sur l'écran des clients, qui n'en fera rien.
  useEffect(() => {
    if (!annuaire.couverture) {
      return;
    }
    let abandonne = false;

    void (async () => {
      const porteur = await jeton();
      if (porteur === null) {
        quandAccesPerdu();
        return;
      }
      const [materiel, zones] = await Promise.all([
        lireVehicules(porteur),
        lireZones(porteur),
      ]);
      if (abandonne) return;

      // Une panne de référentiel ne referme pas la séance et n'affiche pas de
      // bandeau : l'onglet dira « Chargement des référentiels… » et le reste
      // de la fiche reste utilisable.
      if (materiel.etat === 'ok' && zones.etat === 'ok') {
        setReferentiels({ vehicules: materiel.valeur, zones: zones.valeur });
      } else if (materiel.etat === 'echec' && materiel.cause === 'acces') {
        quandAccesPerdu();
      }
    })();

    return () => {
      abandonne = true;
    };
  }, [annuaire, jeton, quandAccesPerdu]);

  /** Repart de ce que l'API vient de rendre — jamais de ce qui était coché. */
  function poserCouverture(rendu: Dossier): void {
    setCouverture({
      vehicules: rendu.vehicules ?? [],
      zones: rendu.zones ?? [],
      envoi: 'aucun',
      refus: null,
    });
  }

  function ouvrir(id: string): void {
    // Choisir une autre ligne referme le formulaire ouvert. La saisie en cours
    // est perdue, sans avertissement : c'est une aspérité connue, à reprendre
    // avec la confirmation de suppression, qui posera la même question.
    setEdition({ nom: 'aucune' });
    setContact({ nom: 'aucune' });
    setAction({ confirme: false, envoi: false, refus: null });
    setFiche({ nom: 'chargement', id });

    void (async () => {
      const porteur = await jeton();
      if (porteur === null) {
        quandAccesPerdu();
        return;
      }
      const resultat = await lireFiche(porteur, annuaire, id);

      // La réponse d'une fiche qu'on n'attend plus est jetée : sans ce
      // contrôle, cliquer vite sur deux lignes peut afficher la première
      // par-dessus la seconde, selon l'ordre d'arrivée des réponses.
      setFiche((courant) => {
        if (courant.nom === 'aucune' || courant.id !== id) {
          return courant;
        }
        if (resultat.etat === 'ok') {
          poserCouverture(resultat.valeur);
          return { nom: 'prete', id, dossier: resultat.valeur };
        }
        if (resultat.cause === 'acces') {
          quandAccesPerdu();
          return courant;
        }
        return { nom: 'panne', id, cause: resultat.cause };
      });
    })();
  }

  function ouvrirCreation(): void {
    const vide = saisieVide(annuaire);
    setFiche({ nom: 'aucune' });
    setEdition({
      nom: 'creation',
      id: null,
      initiale: vide,
      saisie: vide,
      fautes: {},
      refus: null,
      envoi: false,
    });
  }

  function ouvrirModification(): void {
    if (fiche.nom !== 'prete') {
      return;
    }
    const depart = saisieDepuis(fiche.dossier.fiche, annuaire);
    setEdition({
      nom: 'modification',
      id: fiche.dossier.fiche.id,
      initiale: depart,
      saisie: depart,
      fautes: {},
      refus: null,
      envoi: false,
    });
  }

  function changerChamp(cle: string, saisi: string): void {
    setEdition((courant) => {
      if (courant.nom === 'aucune') {
        return courant;
      }
      // La faute du champ disparaît dès qu'on y retouche : la laisser sous un
      // champ qu'on est en train de corriger ne renseigne plus personne.
      const fautes = { ...courant.fautes };
      delete fautes[cle];
      return { ...courant, saisie: { ...courant.saisie, [cle]: saisi }, fautes };
    });
  }

  /**
   * Propose un code, à la création et seulement si le champ est encore vide.
   * On ne touche jamais au code d'une fiche existante : le changer tout seul
   * romprait les rapprochements que quelqu'un fait peut-être avec.
   */
  function completerCode(): void {
    const connus = liste.nom === 'prete' ? liste.lignes : [];
    setEdition((courant) => {
      if (courant.nom !== 'creation' || (courant.saisie['code'] ?? '').trim() !== '') {
        return courant;
      }
      const propose = codePropose(courant.saisie['nom'] ?? '', connus);
      if (propose === '') {
        return courant;
      }
      return { ...courant, saisie: { ...courant.saisie, code: propose } };
    });
  }

  /** Met en mots un refus qui ne vise aucun champ en particulier. */
  function refusEnMots(cause: EchecApi): string {
    if (cause === 'introuvable') {
      return 'Cette fiche n’existe plus. Elle a pu être supprimée entre-temps.';
    }
    if (cause === 'encore_actif') {
      return `Ce ${annuaire.singulier} est encore actif. Désactivez-le avant de le supprimer.`;
    }
    if (cause === 'encore_rattache') {
      // La base a refusé, par ses clés étrangères `on delete restrict`.
      return `Des données sont encore rattachées à ce ${annuaire.singulier} : il ne peut pas être supprimé.`;
    }
    if (cause === 'invalide') {
      // L'écran contrôle avant d'envoyer : arriver ici signifie qu'il est en
      // retard sur l'API. On le dit plutôt que de laisser croire à une panne.
      return 'Stafa a refusé ces informations. Vérifiez les champs saisis.';
    }
    return 'Stafa ne répond pas. Vérifiez votre connexion et réessayez.';
  }

  function enregistrer(): void {
    if (edition.nom === 'aucune' || edition.envoi) {
      return;
    }
    const encours = edition;

    const fautes = validerFiche(encours.saisie, annuaire);
    if (Object.keys(fautes).length > 0) {
      setEdition({ ...encours, fautes, refus: null });
      return;
    }

    const corps =
      encours.nom === 'creation'
        ? pourCreation(encours.saisie, annuaire)
        : difference(encours.initiale, encours.saisie, annuaire);

    // Rien n'a changé : on referme sans rien envoyer. L'API refuserait un corps
    // vide par un 400, ce qui serait un message d'erreur pour un non-événement.
    if (encours.nom === 'modification' && Object.keys(corps).length === 0) {
      setEdition({ nom: 'aucune' });
      return;
    }

    setEdition({ ...encours, envoi: true, refus: null, fautes: {} });

    void (async () => {
      const porteur = await jeton();
      if (porteur === null) {
        quandAccesPerdu();
        return;
      }

      const resultat =
        encours.nom === 'creation'
          ? await creerFiche(porteur, annuaire, corps)
          : await modifierFiche(porteur, annuaire, encours.id ?? '', corps);

      if (resultat.etat === 'ok') {
        const rendu = resultat.valeur;
        setListe((courant) =>
          courant.nom === 'prete'
            ? { nom: 'prete', lignes: fusionner(courant.lignes, rendu.fiche) }
            : courant,
        );
        setEdition({ nom: 'aucune' });
        setFiche({ nom: 'prete', id: rendu.fiche.id, dossier: rendu });
        return;
      }

      if (resultat.cause === 'acces') {
        quandAccesPerdu();
        return;
      }

      if (resultat.cause === 'code_pris') {
        // L'écran nomme l'autre client parce qu'il a déjà la liste entière :
        // aucun appel de plus n'est nécessaire.
        const connus = liste.nom === 'prete' ? liste.lignes : [];
        setEdition({
          ...encours,
          envoi: false,
          fautes: {
            code: messageCodePris(
              connus,
              encours.saisie['code'] ?? '',
              annuaire,
              encours.id ?? undefined,
            ),
          },
          refus: null,
        });
        return;
      }

      setEdition({ ...encours, envoi: false, refus: refusEnMots(resultat.cause) });
    })();
  }

  // -------------------------------------------------------------------------
  // Les contacts
  // -------------------------------------------------------------------------

  /**
   * Applique la fiche rendue par une écriture de contact.
   *
   * Les trois routes rendent la fiche complète : il n'y a donc rien à
   * recharger, et la liste de gauche est remise à jour au passage — un contact
   * ajouté peut faire basculer une fiche de « supprimable » à
   * « encore rattachée ».
   */
  function poserDossier(rendu: Dossier): void {
    poserCouverture(rendu);
    setFiche({ nom: 'prete', id: rendu.fiche.id, dossier: rendu });
    setListe((courant) =>
      courant.nom === 'prete'
        ? { nom: 'prete', lignes: fusionner(courant.lignes, rendu.fiche) }
        : courant,
    );
  }

  /** L'identifiant de la fiche ouverte, s'il y en a une. */
  function ficheOuverte(): string | null {
    return fiche.nom === 'prete' ? fiche.dossier.fiche.id : null;
  }

  function enregistrerContact(): void {
    const ficheId = ficheOuverte();
    if (ficheId === null) {
      return;
    }
    if (contact.nom !== 'creation' && contact.nom !== 'modification') {
      return;
    }
    if (contact.envoi) {
      return;
    }
    const encours = contact;

    const fautes = validerContact(encours.saisie);
    if (Object.keys(fautes).length > 0) {
      setContact({ ...encours, fautes, refus: null });
      return;
    }

    const corps =
      encours.nom === 'creation'
        ? pourCreationContact(encours.saisie)
        : differenceContact(encours.initiale, encours.saisie);

    if (encours.nom === 'modification' && Object.keys(corps).length === 0) {
      setContact({ nom: 'aucune' });
      return;
    }

    setContact({ ...encours, envoi: true, refus: null, fautes: {} });

    void (async () => {
      const porteur = await jeton();
      if (porteur === null) {
        quandAccesPerdu();
        return;
      }
      const resultat =
        encours.nom === 'creation'
          ? await creerContact(porteur, annuaire, ficheId, corps)
          : await modifierContact(porteur, annuaire, ficheId, encours.id ?? '', corps);

      if (resultat.etat === 'ok') {
        poserDossier(resultat.valeur);
        setContact({ nom: 'aucune' });
        return;
      }
      if (resultat.cause === 'acces') {
        quandAccesPerdu();
        return;
      }
      setContact({ ...encours, envoi: false, refus: refusEnMots(resultat.cause) });
    })();
  }

  function supprimerLeContact(): void {
    const ficheId = ficheOuverte();
    if (ficheId === null || contact.nom !== 'suppression' || contact.envoi) {
      return;
    }
    const encours = contact;
    setContact({ ...encours, envoi: true, refus: null });

    void (async () => {
      const porteur = await jeton();
      if (porteur === null) {
        quandAccesPerdu();
        return;
      }
      const resultat = await supprimerContact(porteur, annuaire, ficheId, encours.id);

      if (resultat.etat === 'ok') {
        poserDossier(resultat.valeur);
        setContact({ nom: 'aucune' });
        return;
      }
      if (resultat.cause === 'acces') {
        quandAccesPerdu();
        return;
      }
      setContact({ ...encours, envoi: false, refus: refusEnMots(resultat.cause) });
    })();
  }

  function basculerActivite(): void {
    const ouverte = fiche.nom === 'prete' ? fiche.dossier : null;
    if (ouverte === null || action.envoi) {
      return;
    }
    const vise = !ouverte.fiche.actif;
    setAction({ confirme: false, envoi: true, refus: null });

    void (async () => {
      const porteur = await jeton();
      if (porteur === null) {
        quandAccesPerdu();
        return;
      }
      const resultat = await changerActivite(porteur, annuaire, ouverte.fiche.id, vise);

      if (resultat.etat === 'ok') {
        poserDossier(resultat.valeur);
        setAction({ confirme: false, envoi: false, refus: null });
        return;
      }
      if (resultat.cause === 'acces') {
        quandAccesPerdu();
        return;
      }
      setAction({ confirme: false, envoi: false, refus: refusEnMots(resultat.cause) });
    })();
  }

  function supprimerLaFiche(): void {
    const ouverte = fiche.nom === 'prete' ? fiche.dossier : null;
    if (ouverte === null || action.envoi) {
      return;
    }
    const id = ouverte.fiche.id;
    setAction({ confirme: true, envoi: true, refus: null });

    void (async () => {
      const porteur = await jeton();
      if (porteur === null) {
        quandAccesPerdu();
        return;
      }
      const resultat = await supprimerFiche(porteur, annuaire, id);

      if (resultat.etat === 'ok') {
        // La fiche n'existe plus : on referme le panneau et on retire la ligne.
        setFiche({ nom: 'aucune' });
        setAction({ confirme: false, envoi: false, refus: null });
        setListe((courant) =>
          courant.nom === 'prete'
            ? { nom: 'prete', lignes: courant.lignes.filter((l) => l.id !== id) }
            : courant,
        );
        return;
      }
      if (resultat.cause === 'acces') {
        quandAccesPerdu();
        return;
      }
      setAction({ confirme: true, envoi: false, refus: refusEnMots(resultat.cause) });
    })();
  }

  // -------------------------------------------------------------------------
  // La couverture
  // -------------------------------------------------------------------------

  function cocherVehicule(code: string, coche: boolean): void {
    setCouverture((courant) => ({
      ...courant,
      refus: null,
      vehicules: basculerCode(courant.vehicules, code, coche),
    }));
  }

  function cocherZone(zone: string, sens: 'depart' | 'arrivee', coche: boolean): void {
    setCouverture((courant) => ({
      ...courant,
      refus: null,
      zones: basculerZone(courant.zones, zone, sens, coche),
    }));
  }

  function envoyerCouverture(quoi: 'vehicules' | 'zones'): void {
    const ficheId = ficheOuverte();
    if (ficheId === null || couverture.envoi !== 'aucun') {
      return;
    }
    const encours = couverture;
    setCouverture({ ...encours, envoi: quoi, refus: null });

    void (async () => {
      const porteur = await jeton();
      if (porteur === null) {
        quandAccesPerdu();
        return;
      }
      const resultat =
        quoi === 'vehicules'
          ? await enregistrerVehicules(porteur, annuaire, ficheId, encours.vehicules)
          : await enregistrerZones(porteur, annuaire, ficheId, encours.zones);

      if (resultat.etat === 'ok') {
        // `poserDossier` repose la couverture depuis la fiche rendue : ce que
        // la base a réellement enregistré, et non ce qui était coché.
        poserDossier(resultat.valeur);
        return;
      }
      if (resultat.cause === 'acces') {
        quandAccesPerdu();
        return;
      }
      setCouverture({ ...encours, envoi: 'aucun', refus: refusEnMots(resultat.cause) });
    })();
  }

  const paquetCouverture: PaquetCouverture = {
    referentielVehicules: referentiels.vehicules,
    referentielZones: referentiels.zones,
    edition: couverture,
    quandVehicule: cocherVehicule,
    quandZone: cocherZone,
    quandEnregistrerVehicules: () => envoyerCouverture('vehicules'),
    quandEnregistrerZones: () => envoyerCouverture('zones'),
  };

  const actions: ActionsFiche = {
    confirme: action.confirme,
    envoi: action.envoi,
    refus: action.refus,
    quandBasculerActivite: basculerActivite,
    quandDemanderSuppression: () =>
      setAction({ confirme: true, envoi: false, refus: null }),
    quandSupprimer: supprimerLaFiche,
    quandAnnuler: () => setAction({ confirme: false, envoi: false, refus: null }),
  };

  const contacts: Contacts = {
    edition: contact,
    quandAjouter: () =>
      setContact({
        nom: 'creation',
        id: null,
        initiale: CONTACT_VIDE,
        saisie: CONTACT_VIDE,
        fautes: {},
        refus: null,
        envoi: false,
      }),
    quandModifier: (choisi: Contact) => {
      const depart = contactDepuis(choisi as unknown as Record<string, unknown>);
      setContact({
        nom: 'modification',
        id: choisi.id,
        initiale: depart,
        saisie: depart,
        fautes: {},
        refus: null,
        envoi: false,
      });
    },
    quandDemanderSuppression: (id: string) =>
      setContact({ nom: 'suppression', id, envoi: false, refus: null }),
    quandChamp: (cle: CleChampContact, saisi: string) =>
      setContact((courant) => {
        if (courant.nom !== 'creation' && courant.nom !== 'modification') {
          return courant;
        }
        const fautes = { ...courant.fautes };
        delete fautes[cle];
        return { ...courant, saisie: { ...courant.saisie, [cle]: saisi }, fautes };
      }),
    quandPrincipal: (coche: boolean) =>
      setContact((courant) => {
        if (courant.nom !== 'creation' && courant.nom !== 'modification') {
          return courant;
        }
        return { ...courant, saisie: { ...courant.saisie, principal: coche } };
      }),
    quandEnregistrer: enregistrerContact,
    quandSupprimer: supprimerLeContact,
    quandAnnuler: () => setContact({ nom: 'aucune' }),
  };

  return (
    <VueAnnuaire
      annuaire={annuaire}
      liste={liste}
      fiche={fiche}
      edition={edition}
      onglet={onglet}
      recherche={recherche}
      quandRecherche={setRecherche}
      quandOuvrir={ouvrir}
      quandOnglet={setOnglet}
      quandFermer={() => setFiche({ nom: 'aucune' })}
      quandCreer={ouvrirCreation}
      quandModifier={ouvrirModification}
      quandChamp={changerChamp}
      quandNomQuitte={completerCode}
      quandEnregistrer={enregistrer}
      quandAnnuler={() => setEdition({ nom: 'aucune' })}
      contacts={contacts}
      couverture={paquetCouverture}
      actions={actions}
    />
  );
}
